import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Activity, Droplet, Apple, Trophy, Gift, Brain,
  Smartphone, Heart, TrendingUp, Sparkles, Footprints, Bike, Dumbbell, Moon, CheckCircle2,
  Flame, Target, Repeat, Bell, Lightbulb, Gauge, Megaphone, Users, BookOpen, CalendarDays,
  Eye, Compass, HeartPulse, Check, Clapperboard, Rocket, Coins, Handshake, LayoutGrid,
  ShoppingBag, Plane, Lock,
} from "lucide-react";
import { ASSETS } from "../lib/assets";
import { Overline, Reveal } from "../components/Section";

const trackedActivities = [
  { i: Footprints, t: "Daily step tracking" },
  { i: Activity, t: "Walking & running" },
  { i: Bike, t: "Cycling" },
  { i: Dumbbell, t: "Workouts & fitness activities" },
  { i: Apple, t: "Nutrition tracking" },
  { i: Moon, t: "Sleep monitoring" },
  { i: Droplet, t: "Hydration goals" },
  { i: CheckCircle2, t: "Habit completion" },
  { i: Trophy, t: "Wellness challenges" },
];

const aiFeatures = [
  { i: Brain, t: "Personalized wellness recommendations" },
  { i: Target, t: "Adaptive fitness goals" },
  { i: Repeat, t: "Habit formation guidance" },
  { i: Bell, t: "Smart reminders" },
  { i: Lightbulb, t: "Behavioral insights" },
  { i: TrendingUp, t: "Progress predictions" },
  { i: Gauge, t: "Wellness score analysis" },
  { i: Sparkles, t: "Personalized engagement strategies" },
];

const earnWays = [
  { i: Footprints, t: "Achieving daily step goals" },
  { i: Target, t: "Completing personalized fitness goals" },
  { i: Flame, t: "Maintaining healthy habit streaks" },
  { i: Megaphone, t: "Participating in wellness campaigns" },
  { i: Users, t: "Community fitness challenges" },
  { i: BookOpen, t: "Educational wellness activities" },
  { i: CalendarDays, t: "Special seasonal events" },
];

const rewardCategories = [
  {
    i: Smartphone, t: "Everyday Essentials", c: "from-emerald-500 to-teal-500",
    d: "Redeem Fit Points for everyday digital services that simplify your daily life.",
    ex: ["Mobile Services", "Utility Payments", "Essential Digital Services"],
  },
  {
    i: Clapperboard, t: "Lifestyle & Experiences", c: "from-sky-500 to-blue-500",
    d: "Celebrate your healthy journey with exciting entertainment and memorable experiences.",
    ex: ["Movies", "Travel Experiences", "Events", "Lifestyle Benefits"],
  },
  {
    i: Heart, t: "Health & Wellness", c: "from-rose-500 to-pink-500",
    d: "Invest back into your health with wellness-focused rewards and healthcare benefits.",
    ex: ["Wellness Offers", "Healthcare Benefits", "Insurance Services"],
  },
  {
    i: Gift, t: "Shopping & Gift Rewards", c: "from-violet-500 to-purple-500",
    d: "Enjoy exclusive savings, cashback opportunities, and digital gift rewards from trusted partners.",
    ex: [],
  },
  {
    i: Rocket, t: "Growing Rewards Ecosystem", c: "from-amber-500 to-orange-500",
    d: "Our partner ecosystem continues to expand, bringing more exciting reward opportunities as the Fit2Fin community grows.",
    ex: ["Many More Coming Soon..."],
  },
];

const rewardJourney = [
  { i: Footprints, t: "Healthy Habit" },
  { i: Brain, t: "AI Analysis" },
  { i: Coins, t: "Fit Points Earned" },
  { i: Gift, t: "Unlock Everyday Benefits" },
  { i: HeartPulse, t: "Healthier Lifestyle" },
  { i: Repeat, t: "Repeat & Grow" },
];

const ecosystemHighlights = [
  { i: Handshake, t: "Growing Partner Network" },
  { i: LayoutGrid, t: "Digital Reward Categories" },
  { i: Sparkles, t: "Everyday Benefits" },
  { i: Brain, t: "AI Personalized Rewards" },
  { i: Users, t: "Community Challenges" },
  { i: HeartPulse, t: "Healthy Lifestyle Programs" },
  { i: Rocket, t: "Future Integrations" },
];

const ecosystemNodes = [
  { i: Smartphone, t: "Everyday Essentials" },
  { i: Clapperboard, t: "Entertainment" },
  { i: Heart, t: "Wellness" },
  { i: ShoppingBag, t: "Shopping" },
  { i: Plane, t: "Travel" },
  { i: Users, t: "Community" },
  { i: Handshake, t: "Partner Benefits" },
  { i: Rocket, t: "Future Services" },
];

const habits = [
  "Daily Walking", "Regular Exercise", "Balanced Nutrition", "Healthy Eating",
  "Quality Sleep", "Hydration", "Stress Management", "Mental Wellness",
  "Consistency", "Positive Lifestyle Choices",
];

const dimensions = [
  "Physical Wellness", "Mental Well-being", "Lifestyle Improvement", "Habit Formation",
  "Personal Motivation", "Community Engagement", "Digital Rewards", "Financial Value from Healthy Behaviors",
];

const roadmap = [
  "AI Health Coach", "Nutrition Intelligence", "Family Wellness Programs",
  "Corporate Wellness Solutions", "Community Challenges", "Wearable Device Integrations",
  "Preventive Healthcare Insights", "Insurance & Healthcare Partnerships",
  "Smart Wellness Marketplace", "Personalized Health Journey",
];

const whyChoose = [
  "AI-Powered Personalization",
  "Habit-Based Wellness Approach",
  "Digital Rewards Ecosystem",
  "Gamified User Experience",
  "Secure Health Data Integration",
  "Long-Term Motivation Through Rewards",
  "Built for Individuals, Families & Organizations",
];

// Set to true to restore the full Fit2Fin product details page
const SHOW_DETAILS = false;

export default function Fit2Fin() {
  if (!SHOW_DETAILS) {
    return (
      <div data-testid="fit2fin-page" className="bg-white text-slate-900">
        <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-sky-50">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-emerald-400/30 blur-[120px]" />
            <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-sky-400/30 blur-[120px]" />
          </div>
          <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-32 w-full text-center">
            <Reveal>
              <Link to="/products" data-testid="fit2fin-back-products" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900">
                ← Back to products
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 mx-auto h-36 w-36 rounded-3xl shadow-2xl ring-1 ring-emerald-200 overflow-hidden grid place-items-center"
              >
                <img src={ASSETS.logos.fit2fin} alt="Fit2Fin" className="h-full w-full object-cover" />
              </motion.div>
            </Reveal>
            <Reveal delay={0.2}>
              <Overline color="text-emerald-600" className="mt-10">India's First Habit Platform</Overline>
            </Reveal>
            <Reveal delay={0.25}>
              <h1 className="mt-4 font-display font-black text-[clamp(2.75rem,7vw,5rem)] leading-[0.95] tracking-tighter">
                <span className="text-gradient-fit">Fit2Fin</span><sup className="text-[0.3em] font-bold align-super text-slate-400">™</sup>
              </h1>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-6 font-display text-xl md:text-2xl font-bold text-slate-700">
                Turn Healthy Habits into Meaningful Rewards.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-12 inline-flex items-center gap-3 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-800 font-medium">
                <Lock className="h-4 w-4" />
                Product details coming soon
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.5}>
              <p className="mt-8 max-w-xl mx-auto text-slate-500">
                We&apos;re working on something special. For partnership enquiries, early access requests
                or press, get in touch with our team.
              </p>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="mt-10 flex flex-wrap justify-center gap-3">
                <Link
                  to="/contact"
                  data-testid="fit2fin-cta-contact"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 text-white px-7 py-4 text-sm font-bold hover:opacity-90 transition shadow-[0_10px_40px_rgba(16,185,129,0.35)] hover:-translate-y-0.5"
                >
                  Get in touch <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/products"
                  data-testid="fit2fin-link-products"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-4 text-sm font-medium hover:bg-slate-50 transition"
                >
                  <Sparkles className="h-4 w-4" /> Explore other products
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="fit2fin-page" className="bg-slate-100 text-slate-900">
      {/* HERO */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-emerald-100 via-slate-100 to-sky-100">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-emerald-400/30 blur-[120px]" />
          <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-sky-400/30 blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Link to="/products" data-testid="fit2fin-back-products" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900">
                ← Back to products
              </Link>
            </Reveal>
            <Reveal delay={0.03}>
              <div className="mt-5 inline-flex rounded-full border border-slate-300 bg-white p-1 text-sm shadow-sm" data-testid="fit2fin-tabs">
                <span className="rounded-full bg-slate-900 text-white px-5 py-2 font-semibold" data-testid="fit2fin-tab-overview">Overview</span>
                <Link to="/fit2fin/why-different" data-testid="fit2fin-tab-different" className="tab-attention relative inline-flex items-center gap-2 rounded-full px-5 py-2 font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
                  Why Fit2Fin is Different
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="mt-6 flex items-center gap-4">
                <img src={ASSETS.logos.fit2fin} alt="Fit2Fin" className="h-20 w-20 rounded-2xl shadow-xl ring-1 ring-slate-200" />
                <div>
                  <Overline color="text-emerald-600">India's First Habit Platform</Overline>
                  <div className="font-display text-3xl font-black mt-1">Fit2Fin<sup className="text-xs font-bold align-super text-slate-500">™</sup></div>
                  <div className="mt-1 text-sm font-medium text-slate-500">Turn Healthy Habits into Meaningful Rewards.</div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <h1 className="mt-8 font-display font-black text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-tighter">
                Every Healthy Habit<br />
                <span className="text-gradient-fit">Deserves a Reward.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-8 max-w-xl text-lg text-slate-600 leading-relaxed">
                Fit2Fin™ is an AI-powered digital wellness platform that inspires people to build healthier
                lifestyles by transforming everyday healthy habits into meaningful rewards.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-4 max-w-xl text-slate-500 leading-relaxed">
                More than a fitness app — a habit-building ecosystem powered by Artificial Intelligence,
                gamification, personalized insights, and a digital rewards ecosystem.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  data-testid="fit2fin-cta-early-access"
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-sky-500 text-white px-7 py-4 text-sm font-bold hover:opacity-90 transition shadow-[0_10px_40px_rgba(16,185,129,0.35)] hover:-translate-y-0.5"
                >
                  Join Early Access <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="fit2fin-cta-partner"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-7 py-4 text-sm font-medium hover:bg-slate-800 transition"
                >
                  Become a Partner
                </Link>
                <a
                  href="#how-it-works"
                  data-testid="fit2fin-cta-explore"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-4 text-sm font-medium hover:bg-slate-50 transition"
                >
                  Explore Fit2Fin
                </a>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl ring-1 ring-emerald-200/60">
                  <img src={ASSETS.images.fit2finHero} alt="Fit2Fin lifestyle" className="h-full w-full object-cover" />
                </div>
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute -left-6 top-10 rounded-2xl bg-white shadow-xl ring-1 ring-slate-100 p-4"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Today</div>
                  <div className="font-display text-2xl font-black text-slate-900 mt-1">12,847 steps</div>
                  <div className="text-xs text-emerald-600 font-semibold mt-1">+248 Fit Points</div>
                </motion.div>
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute -right-6 bottom-10 rounded-2xl bg-slate-900 text-white shadow-xl p-4 w-48"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">Wellness Score</div>
                  <div className="flex items-end gap-2 mt-1">
                    <div className="font-display text-3xl font-black">87</div>
                    <div className="text-xs text-emerald-300 mb-1.5">/ 100</div>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-emerald-400 to-sky-400" style={{ width: "87%" }} />
                  </div>
                </motion.div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="relative py-24 bg-slate-100" data-testid="fit2fin-vision-mission">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-10 h-full">
              <div className="h-12 w-12 rounded-xl bg-emerald-100 grid place-items-center text-emerald-600">
                <Eye className="h-6 w-6" />
              </div>
              <Overline color="text-emerald-600" className="mt-6">Our Vision</Overline>
              <h2 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tight leading-tight">
                Make wellness rewarding, engaging & accessible to everyone.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                To inspire millions of people to embrace healthier lifestyles. We envision a future where every
                positive habit — walking more, eating healthier, staying active, or maintaining wellness goals —
                is recognized, encouraged, and rewarded.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-10 h-full">
              <div className="h-12 w-12 rounded-xl bg-sky-100 grid place-items-center text-sky-600">
                <Compass className="h-6 w-6" />
              </div>
              <Overline color="text-sky-600" className="mt-6">Our Mission</Overline>
              <h2 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tight leading-tight">
                Turn small daily actions into lifelong healthy lifestyles.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                To become one of the world's leading AI-powered habit and wellness platforms — helping people
                transform small daily actions into lifelong healthy lifestyles through motivation, technology,
                and meaningful digital rewards.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY FIT2FIN */}
      <section className="relative py-24 bg-slate-200/70 border-y border-slate-300/60" data-testid="fit2fin-why">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Overline color="text-emerald-600">Why Fit2Fin?</Overline>
            <h2 className="mt-4 font-display font-black text-4xl tracking-tighter leading-tight">
              Consistency is the hardest part. <span className="text-gradient-fit">We reward it.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Modern lifestyles have led to increasing physical inactivity, stress, obesity, diabetes, heart
              disease, and other lifestyle-related health challenges. Many people want to live healthier —
              staying consistent is the biggest obstacle.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Fit2Fin bridges this gap by combining behavioral science, Artificial Intelligence, and
              gamification to encourage sustainable healthy habits — not short-term fitness goals.
            </p>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-3xl bg-gradient-to-br from-emerald-500 to-sky-500 text-white p-8 md:p-12">
                <HeartPulse className="h-8 w-8 opacity-90" />
                <p className="mt-6 font-display text-2xl md:text-3xl font-bold leading-snug">
                  "Lasting transformation begins with small, consistent actions — and every positive action
                  deserves recognition."
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="relative py-24 bg-slate-100" data-testid="fit2fin-how-it-works">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-emerald-600">How Fit2Fin Works</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              Your habits, <span className="text-gradient-fit">measured & celebrated.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Fit2Fin securely connects with supported health platforms and wearable devices to monitor your
              wellness activities. AI-driven analytics measure progress, celebrate milestones, and reward you
              for maintaining healthy behaviors.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {trackedActivities.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.05}>
                <div className="group flex items-start gap-4 rounded-2xl bg-white border border-slate-200 p-6 hover:border-emerald-300 hover:shadow-lg transition">
                  <div className="h-11 w-11 rounded-xl bg-emerald-100 grid place-items-center text-emerald-600 shrink-0 group-hover:bg-emerald-500 group-hover:text-white transition">
                    <f.i className="h-5 w-5" />
                  </div>
                  <div className="font-medium text-slate-900 leading-tight pt-2">{f.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI POWERED */}
      <section className="relative py-24 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-950 text-white overflow-hidden" data-testid="fit2fin-ai">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute -top-32 right-1/4 h-96 w-96 rounded-full bg-emerald-500/20 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Overline color="text-emerald-400">AI-Powered Personal Wellness</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              A wellness experience that <span className="text-gradient-fit">learns you.</span>
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              Artificial Intelligence powers the entire Fit2Fin experience. As you grow, Fit2Fin continuously
              learns from your activity patterns to deliver increasingly personalized experiences.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {aiFeatures.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.05}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-6">
                  <div className="h-10 w-10 rounded-lg bg-emerald-400/15 grid place-items-center text-emerald-300 mb-4">
                    <c.i className="h-5 w-5" />
                  </div>
                  <div className="font-medium text-white">{c.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FIT POINTS ECOSYSTEM */}
      <section className="relative py-24 bg-slate-100" data-testid="fit2fin-fit-points">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Overline color="text-emerald-600">The Fit Points Ecosystem</Overline>
            <h2 className="mt-4 font-display font-black text-4xl tracking-tighter leading-tight">
              Earn Fit Points for <span className="text-gradient-fit">positive habits.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Fit Points are earned by adopting positive lifestyle habits and participating in wellness
              activities — then redeemed for a growing range of digital benefits through trusted partners.
            </p>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {earnWays.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.05}>
                <div className="group flex items-start gap-4 rounded-2xl bg-white border border-slate-200 p-6 hover:border-emerald-300 hover:shadow-lg transition">
                  <div className="h-11 w-11 rounded-xl bg-emerald-100 grid place-items-center text-emerald-600 shrink-0 group-hover:bg-emerald-500 group-hover:text-white transition">
                    <f.i className="h-5 w-5" />
                  </div>
                  <div className="font-medium text-slate-900 leading-tight pt-2">{f.t}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REWARDS ECOSYSTEM — Healthy Habits First. Rewards Second. */}
      <section className="relative py-24 bg-slate-200/70 border-y border-slate-300/60" data-testid="fit2fin-rewards-ecosystem">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          {/* "Unlock Everyday Benefits" heading + 5 category cards — hidden per user request. Set to true to restore. */}
          {false && (
            <>
              <div className="text-center max-w-3xl mx-auto">
                <Overline color="text-emerald-600">Healthy Habits First. Rewards Second.</Overline>
                <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
                  Unlock <span className="text-gradient-fit">Everyday Benefits</span>
                </h2>
                <p className="mt-5 text-slate-600 leading-relaxed">
                  Healthy habits deserve meaningful rewards. Every step you take, every goal you achieve, and
                  every healthy choice you make brings you closer to valuable everyday benefits through the
                  Fit2Fin Rewards Ecosystem.
                </p>
              </div>
              <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="fit2fin-reward-categories">
                {rewardCategories.map((c, i) => (
                  <Reveal key={c.t} delay={i * 0.07}>
                    <div className="group relative rounded-3xl border border-white/70 bg-white/60 backdrop-blur-xl p-8 h-full shadow-[0_8px_40px_rgba(15,23,42,0.06)] hover:shadow-[0_16px_60px_rgba(16,185,129,0.15)] hover:-translate-y-1 transition overflow-hidden">
                      <div className={`absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-to-br ${c.c} opacity-10 blur-2xl group-hover:opacity-20 transition`} />
                      <div className={`relative h-12 w-12 rounded-2xl bg-gradient-to-br ${c.c} grid place-items-center text-white shadow-lg`}>
                        <c.i className="h-6 w-6" />
                      </div>
                      <div className="relative mt-5 font-display text-xl font-black text-slate-900">{c.t}</div>
                      <p className="relative mt-3 text-sm text-slate-600 leading-relaxed">{c.d}</p>
                      {c.ex.length > 0 && (
                        <div className="relative mt-5 flex flex-wrap gap-2">
                          {c.ex.map((e) => (
                            <span key={e} className="rounded-full bg-white border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600">
                              {e}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Reveal>
                ))}
              </div>
            </>
          )}

          {/* Journey infographic */}
          <div data-testid="fit2fin-reward-journey">
            <div className="text-center">
              <Overline color="text-emerald-600">From Habit to Benefit</Overline>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-y-8 gap-x-3">
              {rewardJourney.map((s, i) => (
                <Reveal key={s.t} delay={i * 0.08}>
                  <div className="relative flex flex-col items-center text-center">
                    <motion.div
                      whileHover={{ scale: 1.08 }}
                      className="h-14 w-14 rounded-2xl bg-white border border-slate-200 shadow-md grid place-items-center text-emerald-600"
                    >
                      <s.i className="h-6 w-6" />
                    </motion.div>
                    <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">{String(i + 1).padStart(2, "0")}</div>
                    <div className="mt-1 font-semibold text-slate-900 text-sm max-w-[150px]">{s.t}</div>
                    {i < rewardJourney.length - 1 && (
                      <ArrowRight className="hidden lg:block absolute top-4 -right-3 h-5 w-5 text-emerald-400" />
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Emotional message */}
          <Reveal delay={0.1}>
            <div className="mt-20 rounded-[2rem] bg-slate-900 text-white p-10 md:p-14 text-center" data-testid="fit2fin-reward-message">
              <p className="font-display text-3xl md:text-4xl font-black tracking-tight leading-tight">
                Rewards are not the destination.<br />
                <span className="text-gradient-fit">They are the motivation.</span>
              </p>
              <p className="mt-6 max-w-2xl mx-auto text-slate-300 leading-relaxed">
                At Fit2Fin, we believe that the greatest reward is becoming healthier every day. Our digital
                rewards simply celebrate your commitment to building lifelong healthy habits.
              </p>
            </div>
          </Reveal>

          {/* Ecosystem highlights */}
          <div className="mt-16" data-testid="fit2fin-ecosystem-highlights">
            <div className="flex flex-wrap justify-center gap-3">
              {ecosystemHighlights.map((h, i) => (
                <Reveal key={h.t} delay={i * 0.04}>
                  <div className="inline-flex items-center gap-2.5 rounded-full bg-white/70 backdrop-blur border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 hover:border-emerald-300 transition">
                    <h.i className="h-4 w-4 text-emerald-600" /> {h.t}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* THE FIT2FIN REWARDS ECOSYSTEM */}
          <Reveal delay={0.1}>
            <div className="mt-20 relative rounded-[2.5rem] bg-slate-950 text-white p-8 md:p-14 overflow-hidden" data-testid="fit2fin-ecosystem-map">
              <div className="absolute inset-0 grid-bg opacity-10" />
              <div className="absolute top-0 left-1/2 -translate-x-1/2 h-64 w-[500px] rounded-full bg-emerald-500/15 blur-[100px]" />
              <div className="relative text-center max-w-2xl mx-auto">
                <Overline color="text-emerald-400">The Fit2Fin Rewards Ecosystem</Overline>
                <h3 className="mt-4 font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight">
                  One currency. <span className="text-gradient-fit">Endless motivation.</span>
                </h3>
              </div>

              {/* Desktop connected ecosystem */}
              <div className="hidden lg:block relative mx-auto mt-14 h-[560px] w-[560px]">
                <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
                  {ecosystemNodes.map((n, i) => {
                    const angle = (i / ecosystemNodes.length) * 2 * Math.PI - Math.PI / 2;
                    const x = 50 + 41 * Math.cos(angle);
                    const y = 50 + 41 * Math.sin(angle);
                    return (
                      <motion.line
                        key={n.t}
                        x1="50" y1="50" x2={x} y2={y}
                        stroke="rgba(52,211,153,0.35)" strokeWidth="0.35" strokeDasharray="1.6 1.2"
                        initial={{ pathLength: 0, opacity: 0 }}
                        whileInView={{ pathLength: 1, opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: 0.2 + i * 0.08 }}
                      />
                    );
                  })}
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                  <motion.div
                    animate={{ scale: [1, 1.04, 1] }} transition={{ duration: 3, repeat: Infinity }}
                    className="h-36 w-36 rounded-full bg-gradient-to-br from-emerald-500 to-sky-500 grid place-items-center text-center shadow-[0_0_60px_rgba(16,185,129,0.45)]"
                  >
                    <div>
                      <Coins className="h-7 w-7 mx-auto" />
                      <div className="mt-1 font-display font-black text-xl">Fit Points</div>
                    </div>
                  </motion.div>
                </div>
                {ecosystemNodes.map((n, i) => {
                  const angle = (i / ecosystemNodes.length) * 2 * Math.PI - Math.PI / 2;
                  const x = 50 + 41 * Math.cos(angle);
                  const y = 50 + 41 * Math.sin(angle);
                  return (
                    <Reveal key={n.t} delay={0.3 + i * 0.06}>
                      <div
                        className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 w-32 text-center"
                        style={{ left: `${x}%`, top: `${y}%` }}
                      >
                        <div className="h-12 w-12 rounded-2xl bg-slate-900 border border-emerald-500/30 grid place-items-center text-emerald-300 shadow-lg backdrop-blur">
                          <n.i className="h-5 w-5" />
                        </div>
                        <div className="text-xs font-semibold leading-snug">{n.t}</div>
                      </div>
                    </Reveal>
                  );
                })}
              </div>

              {/* Mobile ecosystem */}
              <div className="lg:hidden mt-12">
                <div className="mx-auto h-28 w-28 rounded-full bg-gradient-to-br from-emerald-500 to-sky-500 grid place-items-center text-center shadow-[0_0_50px_rgba(16,185,129,0.4)]">
                  <div>
                    <Coins className="h-6 w-6 mx-auto" />
                    <div className="mt-0.5 font-display font-black text-sm">Fit Points</div>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-2 gap-3">
                  {ecosystemNodes.map((n) => (
                    <div key={n.t} className="flex items-center gap-3 rounded-2xl border border-emerald-500/20 bg-slate-900/80 p-4">
                      <n.i className="h-5 w-5 text-emerald-300 shrink-0" />
                      <span className="text-sm font-semibold">{n.t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Rewards CTA banner */}
          <Reveal delay={0.1}>
            <div className="mt-16 rounded-[2rem] bg-gradient-to-br from-emerald-500 to-sky-500 text-white p-10 md:p-14 text-center" data-testid="fit2fin-rewards-cta">
              <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight max-w-3xl mx-auto">
                Every Healthy Habit Unlocks New Possibilities
              </h3>
              <p className="mt-5 max-w-2xl mx-auto text-emerald-50/90 leading-relaxed">
                Healthy living should never feel like a burden. With Fit2Fin, every positive habit moves you
                toward a healthier lifestyle while unlocking valuable everyday benefits.
              </p>
              <Link
                to="/contact"
                data-testid="fit2fin-rewards-cta-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-emerald-700 px-8 py-4 text-sm font-bold hover:bg-emerald-50 transition shadow-xl"
              >
                Start Your Healthy Journey <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HEALTHY HABITS + MORE THAN FITNESS */}
      <section className="relative py-24 bg-slate-100" data-testid="fit2fin-habits">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <Overline color="text-emerald-600">Healthy Habits We Encourage</Overline>
            <h2 className="mt-4 font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight">
              Progress — <span className="text-gradient-fit">not perfection.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Fit2Fin motivates users to adopt lifelong healthy habits, celebrating every step forward and
              making wellness achievable for everyone.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {habits.map((h) => (
                <span key={h} className="rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:border-emerald-300 transition">
                  {h}
                </span>
              ))}
            </div>
          </div>
          <div>
            <Overline color="text-sky-600">More Than Fitness</Overline>
            <h2 className="mt-4 font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight">
              A complete <span className="text-gradient-fit">lifestyle platform.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Fit2Fin is not limited to counting steps — it helps people improve multiple dimensions of their
              lives.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {dimensions.map((d, i) => (
                <Reveal key={d} delay={i * 0.04}>
                  <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-700 font-medium">{d}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FUTURE INNOVATION */}
      <section className="relative py-24 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-950 text-white overflow-hidden" data-testid="fit2fin-roadmap">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <Overline color="text-emerald-400">Future Innovation</Overline>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              The road <span className="text-gradient-fit">ahead.</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {roadmap.map((r, i) => (
              <Reveal key={r} delay={i * 0.04}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-5 h-full">
                  <div className="font-mono text-emerald-400 text-xs">{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-2 font-medium text-white text-sm leading-snug">{r}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT + WHY CHOOSE */}
      <section className="relative py-24 bg-slate-100" data-testid="fit2fin-why-choose">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <Overline color="text-emerald-600">Our Impact</Overline>
            <h2 className="mt-4 font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight">
              Healthier people. Stronger communities.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Fit2Fin aims to create lasting social impact by encouraging healthier lifestyles, reducing
              preventable lifestyle diseases, and making wellness enjoyable through technology and rewards.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Every step, every habit, and every achievement contributes to a healthier individual, a stronger
              community, and a more productive society.
            </p>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-8">
              <Overline color="text-sky-600">Why Choose Fit2Fin?</Overline>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {whyChoose.map((t, i) => (
                  <Reveal key={t} delay={i * 0.04}>
                    <div className="flex items-start gap-3 rounded-2xl bg-slate-50 border border-slate-200 p-4">
                      <div className="h-5 w-5 rounded-full bg-emerald-500 text-white grid place-items-center shrink-0 mt-0.5">
                        <Check className="h-3 w-3" />
                      </div>
                      <div className="text-sm text-slate-700 font-medium">{t}</div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-gradient-to-br from-emerald-500 to-sky-500 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-4 py-2 text-sm font-semibold backdrop-blur">
            🇮🇳 India's First Habit Platform
          </div>
          <h2 className="mt-8 font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05] max-w-4xl mx-auto">
            Every Step Counts. Every Habit Rewards. Every Life Matters.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-emerald-50/90 leading-relaxed">
            The true reward isn't a mobile recharge or a movie ticket. The true reward is becoming healthier,
            happier, and stronger every single day. Fit2Fin simply celebrates your journey.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              data-testid="fit2fin-cta-bottom-early"
              className="inline-flex items-center gap-2 rounded-full bg-white text-emerald-700 px-7 py-4 text-sm font-bold hover:bg-emerald-50 transition shadow-xl"
            >
              Join Early Access <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="fit2fin-cta-bottom-partner"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900/30 text-white border border-white/30 px-7 py-4 text-sm font-medium hover:bg-slate-900/50 transition backdrop-blur"
            >
              Become a Partner
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
